/**
 * `heysegment session start` — authorize a CLI session via the browser handoff.
 *
 * Flow: resolve config → PKCE → `initiate` → open the authorize URL → poll until
 * the browser flow completes → save the session to `.heysegment/` (identity) and
 * `~/.heysegment/credentials.json` (token). The session token never appears in a
 * URL; it arrives only through the verifier-gated poll.
 *
 * @module
 */
import { Console, Duration, Effect, Option, Schema as S } from "effect"
import { Command, Flag } from "effect/unstable/cli"
import { TemplateIdSchema, type TemplateId } from "@repo/templates"
import { OperationFailed, UsageError } from "../errors"
import { resolveProjectDir } from "../project"
import { codeChallengeOf, generateVerifier } from "../session/pkce"
import { openBrowser } from "../session/browser"
import {
  ensureGitignored,
  normalizeBaseUrl,
  readSessionFile,
  resolveBaseUrl,
  writeCredential,
  writeSessionFile,
} from "../session/metadata"
import { HandoffClient, handoffLayer } from "../rpc"

// ── Flags ────────────────────────────────────────────────────────────────────

const dirFlag = Flag.string("dir").pipe(
  Flag.withDescription("Project directory (default: current directory)"),
  Flag.withDefault("."),
)
const apiUrlFlag = Flag.string("api-url").pipe(
  Flag.withDescription("API base URL override, e.g. http://localhost:3000"),
  Flag.optional,
)
const noOpenFlag = Flag.boolean("no-open").pipe(
  Flag.withDescription("Print the authorize URL instead of opening a browser"),
)
const forceFlag = Flag.boolean("force").pipe(
  Flag.withDescription("Re-authorize even if a local session already exists"),
)

// ── Handoff poll loop ────────────────────────────────────────────────────────

interface ReadySession {
  readonly sessionId: string
  readonly sessionToken: string
  readonly workspaceId: string
  readonly versionId: string
}

const pollUntilReady = (input: {
  readonly handoffCode: string
  readonly verifier: string
  readonly intervalMs: number
  readonly expiresAt: number
}): Effect.Effect<ReadySession, OperationFailed, HandoffClient> =>
  Effect.gen(function* () {
    const client = yield* HandoffClient

    const poll = (): Effect.Effect<ReadySession, OperationFailed> =>
      Effect.gen(function* () {
        if (Date.now() > input.expiresAt) {
          return yield* Effect.fail(new OperationFailed({ failed: ["handoff:timeout"] }))
        }
        const result = yield* client
          .poll({ handoffCode: input.handoffCode, codeVerifier: input.verifier })
          .pipe(Effect.mapError(() => new OperationFailed({ failed: ["handoff:poll"] })))

        if (result.status === "ready") {
          return {
            sessionId: result.sessionId,
            sessionToken: result.sessionToken,
            workspaceId: result.workspaceId,
            versionId: result.versionId,
          }
        }
        if (result.status === "expired") {
          return yield* Effect.fail(new OperationFailed({ failed: ["handoff:expired"] }))
        }
        yield* Effect.sleep(Duration.millis(input.intervalMs))
        return yield* poll()
      })

    return yield* poll()
  })

const runHandoff = (input: {
  readonly codeChallenge: string
  readonly verifier: string
  readonly noOpen: boolean
}) =>
  Effect.gen(function* () {
    const client = yield* HandoffClient
    const init = yield* client
      .initiate({ codeChallenge: input.codeChallenge })
      .pipe(Effect.mapError(() => new OperationFailed({ failed: ["handoff:initiate"] })))

    yield* Console.log("")
    yield* Console.log("To authorize this session, open:")
    yield* Console.log(`  ${init.browserUrl}`)
    if (!input.noOpen) {
      yield* openBrowser(init.browserUrl)
    }
    yield* Console.log("")
    yield* Console.log("Waiting for you to finish in the browser…")

    return yield* pollUntilReady({
      handoffCode: init.handoffCode,
      verifier: input.verifier,
      intervalMs: init.pollIntervalMs,
      expiresAt: init.expiresAt,
    })
  })

// ── Command ──────────────────────────────────────────────────────────────────

export const startCommand = Command.make(
  "start",
  { dir: dirFlag, apiUrl: apiUrlFlag, noOpen: noOpenFlag, force: forceFlag },
  (flags) =>
    Effect.gen(function* () {
      const projectDir = yield* resolveProjectDir(flags.dir)
      const existing = yield* readSessionFile(projectDir)
      const baseUrl = resolveBaseUrl(Option.getOrUndefined(flags.apiUrl), existing)

      if (existing !== null && !flags.force) {
        yield* Console.log(
          `Already authorized for workspace ${existing.workspaceId} (version ${existing.versionId}).`,
        )
        yield* Console.log("Run `heysegment session start --force` to re-authorize.")
        return
      }

      const verifier = generateVerifier()
      const codeChallenge = yield* codeChallengeOf(verifier)

      const session = yield* runHandoff({
        codeChallenge,
        verifier,
        noOpen: flags.noOpen,
      }).pipe(Effect.provide(handoffLayer(baseUrl)))

      const now = new Date().toISOString()
      const sessionFile = yield* writeSessionFile(projectDir, {
        schemaVersion: 1,
        sessionId: session.sessionId,
        workspaceId: session.workspaceId,
        versionId: session.versionId,
        apiBaseUrl: baseUrl,
        createdAt: now,
      })
      const credentialFile = yield* writeCredential({
        baseUrl,
        sessionId: session.sessionId,
        token: session.sessionToken,
        savedAt: now,
      })
      yield* ensureGitignored(projectDir)

      yield* Console.log("")
      yield* Console.log(
        `✓ Session ready — workspace ${session.workspaceId}, version ${session.versionId}.`,
      )
      yield* Console.log(`  Local session identity: ${sessionFile}`)
      yield* Console.log(`  Session token: ${credentialFile}`)
      yield* Console.log(
        "  .heysegment/ is excluded from heysegment push and added to .gitignore when possible.",
      )
    }),
).pipe(
  Command.withDescription("Authorize a CLI session in the browser and save it locally"),
)

// ── `session adopt` ────────────────────────────────────────────────────────

/**
 * The server-minted session code: base64 of this JSON, carried as one string in
 * the VTM boot payload. Decoded with a Schema (never cast) before it touches disk.
 */
const SessionCode = S.Struct({
  sessionId: S.String,
  sessionToken: S.String,
  workspaceId: S.String,
  versionId: S.String,
  apiBaseUrl: S.String,
  templateId: S.optional(TemplateIdSchema),
})
const decodeSessionCode = S.decodeUnknownEffect(SessionCode)
const decodeTemplateId = S.decodeUnknownEffect(TemplateIdSchema)
export type AdoptSessionCode = typeof SessionCode.Type

export const decodeAdoptSessionCode = (
  encoded: string,
  requestedTemplate: string | undefined,
): Effect.Effect<AdoptSessionCode, UsageError> =>
  Effect.gen(function* () {
    const decodedJson = yield* Effect.try({
      try: () => Buffer.from(encoded, "base64").toString("utf8"),
      catch: () => new UsageError({ reason: "Session code is not valid base64" }),
    })
    const parsed = yield* Effect.try({
      try: () => JSON.parse(decodedJson),
      catch: () => new UsageError({ reason: "Session code did not contain valid JSON" }),
    })
    const code = yield* decodeSessionCode(parsed).pipe(
      Effect.mapError(
        () => new UsageError({ reason: "Session code is missing required fields" }),
      ),
    )
    const templateId: TemplateId | undefined = requestedTemplate === undefined
      ? undefined
      : yield* decodeTemplateId(requestedTemplate).pipe(
          Effect.mapError(() => new UsageError({ reason: `Unknown template: ${requestedTemplate}` })),
        )

    if (templateId !== undefined && code.templateId !== templateId) {
      return yield* Effect.fail(new UsageError({
        reason: code.templateId === undefined
          ? "This session is not associated with a starter template"
          : `Template mismatch: session uses ${code.templateId}, not ${templateId}`,
      }))
    }
    return code
  })

const codeFlag = Flag.string("code").pipe(
  Flag.withDescription("Base64 session code minted by the server (no browser handoff)"),
)
const templateFlag = Flag.string("template").pipe(
  Flag.withDescription("Verify the starter template bound to this session"),
  Flag.optional,
)

export const adoptCommand = Command.make(
  "adopt",
  { dir: dirFlag, code: codeFlag, template: templateFlag },
  (flags) =>
    Effect.gen(function* () {
      const projectDir = yield* resolveProjectDir(flags.dir)

      const code = yield* decodeAdoptSessionCode(
        flags.code,
        Option.getOrUndefined(flags.template),
      )

      const baseUrl = normalizeBaseUrl(code.apiBaseUrl)
      const now = new Date().toISOString()
      const sessionFile = yield* writeSessionFile(projectDir, {
        schemaVersion: 1,
        sessionId: code.sessionId,
        workspaceId: code.workspaceId,
        versionId: code.versionId,
        apiBaseUrl: baseUrl,
        createdAt: now,
      })
      const credentialFile = yield* writeCredential({
        baseUrl,
        sessionId: code.sessionId,
        token: code.sessionToken,
        savedAt: now,
      })
      yield* ensureGitignored(projectDir)

      yield* Console.log(
        `✓ Adopted session — workspace ${code.workspaceId}, version ${code.versionId}.`,
      )
      yield* Console.log(`  Local session identity: ${sessionFile}`)
      yield* Console.log(`  Session token: ${credentialFile}`)
      yield* Console.log(
        "  .heysegment/ is excluded from heysegment push and added to .gitignore when possible.",
      )
    }),
).pipe(
  Command.withDescription(
    "Adopt a server-minted session from a base64 code and save it locally (no browser handoff)",
  ),
)

export const sessionCommand = Command.make("session").pipe(
  Command.withDescription("Manage CLI coding sessions"),
  Command.withSubcommands([startCommand, adoptCommand]),
)
